import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Search, X, Globe, MapPin, CalendarDays, Loader2, Heart, ChevronLeft, ChevronRight, Plus, ChevronDown, Building2 } from "lucide-react";
import { SocialMediaIcons } from "@/components/SocialMediaIcons";
import { formatHoursDisplay } from "@/components/HoursSelector";
import { useAuth } from "@/contexts/AuthContext";
import { BusinessDetailModal } from "@/pages/DirectoryPage";
import { normalizeUrl } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 9;
const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http") || logo.startsWith("data:") || logo.startsWith("blob:")) return logo;
  const baseUrl = BACKEND_URL || window.location.origin;
  try {
    return new URL(logo, baseUrl).toString();
  } catch {
    return logo.startsWith("/") ? `${baseUrl.replace(/\/$/, "")}${logo}` : logo;
  }
};

const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const POPUP_FILTER = "Pop-up";
const TRUE_VALUES = new Set(["true", "1", "yes", "y", "on"]);
const YEAR_ROUND_VALUES = new Set([
  ...TRUE_VALUES,
  "year-round",
  "year round",
  "year-round market",
  "year round market",
]);

const CHIP_THEMES = [
  "border-[#42D78A]/35 bg-[#42D78A]/10 text-[#157347] hover:border-[#42D78A] hover:bg-[#42D78A]/20",
  "border-[#AFC8FF]/70 bg-[#AFC8FF]/20 text-[#36558F] hover:border-[#AFC8FF] hover:bg-[#AFC8FF]/35",
  "border-[#FFE8A3] bg-[#FFE8A3]/35 text-[#7A5B00] hover:border-[#F2C94C] hover:bg-[#FFE8A3]/60",
  "border-[#F6B6D5]/70 bg-[#F6B6D5]/20 text-[#8A2F5A] hover:border-[#F6B6D5] hover:bg-[#F6B6D5]/35",
];

const getChipClasses = (selected, index) => `px-3 py-1.5 text-xs font-semibold border rounded-full transition-all shadow-sm ${
  selected ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : CHIP_THEMES[index % CHIP_THEMES.length]
}`;

const FilterGroup = ({ title, open, onToggle, count, children }) => (
  <div className="mb-8">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 text-left"
      aria-expanded={open}
    >
      <span className="min-w-0 text-[13px] font-bold text-[#1a1a1a] uppercase tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {title}
      </span>
      <span className="flex items-center gap-2">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">{count}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </span>
    </button>
    {open && <div className="mt-3 flex flex-wrap gap-2">{children}</div>}
  </div>
);

const valueMeansTrue = (value) => {
  if (value === true) return true;
  if (typeof value === "number") return value === 1;
  if (typeof value !== "string") return false;
  return TRUE_VALUES.has(value.trim().toLowerCase());
};

const valueMeansYearRound = (value) => {
  if (valueMeansTrue(value)) return true;
  if (typeof value !== "string") return false;
  return YEAR_ROUND_VALUES.has(value.trim().toLowerCase());
};

const marketIsYearRound = (market) => {
  if (!market) return false;
  if (typeof market.season_year_round === "boolean") return market.season_year_round;
  if (valueMeansYearRound(market.season_year_round)) return true;
  if (valueMeansYearRound(market.market_status)) return true;
  if (Array.isArray(market.tags)) return market.tags.some(valueMeansYearRound);
  return false;
};

const marketIsOpenOnDay = (market, day) => {
  if (day === POPUP_FILTER) return marketIsPopUp(market);
  if (!market?.hours || typeof market.hours !== "object") return false;
  return !!market.hours?.[day]?.open;
};

const formatSeason = (market) => {
  if (marketIsYearRound(market)) return "Year-round";
  const start = market?.season_start;
  const end = market?.season_end;
  const fmt = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const s = fmt(start);
  const e = fmt(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
};

const firstOpenDay = (hours) => {
  if (!hours || typeof hours !== "object") return null;
  for (const d of DAY_OPTIONS) {
    if (hours[d]?.open) return { day: d, from: hours[d].from, to: hours[d].to };
  }
  return null;
};

const formatMarketDay = (market, openDay) => {
  if (market?.day) return market.day;
  if (openDay?.day) return `${openDay.day}s`;
  if (marketIsPopUp(market)) return POPUP_FILTER;
  return "";
};

const marketIsPopUp = (market) => {
  if (!valueMeansTrue(market?.hours_event_based)) return false;
  return !market?.day && !firstOpenDay(market?.hours);
};

const filtersDefaultOpen = () => (
  typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches
);

const MarketDirectoryPage = () => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [showYearRound, setShowYearRound] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [savedMarketIds, setSavedMarketIds] = useState([]);
  const [openFilters, setOpenFilters] = useState(() => {
    const open = filtersDefaultOpen();
    return { openOn: open, season: open };
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const isShopper = user?.userType === "shopper";
  const isAdmin = !!localStorage.getItem("bizkeep_admin_token");

  const toggleFilterGroup = (group) => {
    setOpenFilters((previous) => ({ ...previous, [group]: !previous[group] }));
  };

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await axios.get(`${API}/markets/directory`);
        setMarkets(response.data);
      } catch (error) {
        console.error("Failed to fetch markets:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMarkets();
  }, []);

  useEffect(() => {
    const fetchShopper = async () => {
      if (!isShopper || !user?.token) return;
      try {
        const response = await axios.get(`${API}/shopper`, { headers: { Authorization: `Bearer ${user.token}` } });
        setSavedMarketIds(response.data.saved_market_ids || []);
      } catch {
        setSavedMarketIds([]);
      }
    };
    fetchShopper();
  }, [isShopper, user?.token]);

  useEffect(() => {
    if (loading) return;
    const marketId = searchParams.get("market");
    if (!marketId) {
      if (selectedMarket) setSelectedMarket(null);
      return;
    }
    const market = markets.find((item) => item.id === marketId);
    if (market && selectedMarket?.id !== marketId) {
      setSelectedMarket(market);
    }
  }, [markets, loading, searchParams, selectedMarket?.id]);

  const openMarketModal = (market) => {
    setSelectedMarket(market);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("market", market.id);
    setSearchParams(nextParams);
  };

  const closeMarketModal = () => {
    setSelectedMarket(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("market");
    setSearchParams(nextParams);
  };

  const handleFollowMarket = async (marketId) => {
    if (!isAuthenticated) {
      window.location.href = "/register";
      return;
    }
    if (!isShopper) {
      toast.error("Use a shopper profile to follow markets.");
      return;
    }

    try {
      const response = await axios.post(`${API}/shopper/follow/market/${marketId}`, {}, { headers: { Authorization: `Bearer ${user.token}` } });
      setSavedMarketIds(response.data.saved_market_ids || []);
      toast.success(response.data.following ? "Market followed." : "Market removed.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update followed market");
    }
  };

  const availableOpenFilters = useMemo(() => {
    const set = new Set();
    markets.forEach((m) => {
      if (m.hours && typeof m.hours === "object") {
        DAY_OPTIONS.forEach((d) => {
          if (m.hours[d]?.open) set.add(d);
        });
      }
      if (marketIsPopUp(m)) set.add(POPUP_FILTER);
    });
    return [...DAY_OPTIONS.filter((d) => set.has(d)), ...(set.has(POPUP_FILTER) ? [POPUP_FILTER] : [])];
  }, [markets]);

  const filteredMarkets = useMemo(() => {
    return markets.filter((m) => {
      const matchesSearch = !searchTerm ||
        m.market_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.address?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDay = !selectedDay || marketIsOpenOnDay(m, selectedDay);
      const matchesYearRound = !showYearRound || marketIsYearRound(m);
      return matchesSearch && matchesDay && matchesYearRound;
    });
  }, [markets, searchTerm, selectedDay, showYearRound]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDay, showYearRound]);

  const pageCount = Math.max(1, Math.ceil(filteredMarkets.length / PAGE_SIZE));
  const currentSafePage = Math.min(currentPage, pageCount);
  const visibleMarkets = filteredMarkets.slice(
    (currentSafePage - 1) * PAGE_SIZE,
    currentSafePage * PAGE_SIZE
  );

  const clearFilters = () => { setSelectedDay(null); setShowYearRound(false); setSearchTerm(""); setCurrentPage(1); };

  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-16 md:py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Directory</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Market Directory</h1>
          <p className="text-gray-600 text-sm tracking-[0.15em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Discover local markets in Columbus
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/directory">
              <Button
                className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="market-directory-business-link"
              >
                View Business Directory
              </Button>
            </Link>
            <Link to="/events">
              <Button
                variant="outline"
                className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="market-directory-events-link"
              >
                See Upcoming Events
              </Button>
            </Link>
            {isAdmin && (
              <Link to="/admin/market/new">
                <Button
                  variant="outline"
                  className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="market-directory-admin-add-market-link"
                >
                  <Plus className="w-4 h-4" /> Add Market
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        {/* Search */}
        <div className="relative mb-8 max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input type="text" placeholder="Search markets..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-12 h-12 border-gray-200 rounded-full text-base shadow-sm" data-testid="market-search-input" />
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          {/* Sidebar Filters */}
          <aside className="w-full md:w-52 md:max-w-52 flex-shrink-0">
            <FilterGroup
              title="Open on"
              count={availableOpenFilters.length}
              open={openFilters.openOn}
              onToggle={() => toggleFilterGroup("openOn")}
            >
              {availableOpenFilters.length === 0 ? (
                <p className="text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>No markets with set hours yet.</p>
              ) : (
                <>
                  {availableOpenFilters.map((day, index) => (
                    <button key={day} onClick={() => setSelectedDay(selectedDay === day ? null : day)}
                      className={getChipClasses(selectedDay === day, index)}
                      style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid={`day-filter-${day.toLowerCase().replace(/\s+/g, '-')}`}>{day}</button>
                  ))}
                </>
              )}
            </FilterGroup>
            <FilterGroup
              title="Season"
              count={1}
              open={openFilters.season}
              onToggle={() => toggleFilterGroup("season")}
            >
              <button
                onClick={() => setShowYearRound((current) => !current)}
                className={getChipClasses(showYearRound, 1)}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                data-testid="year-round-market-filter"
              >
                Year-round markets
              </button>
            </FilterGroup>
            {(selectedDay || showYearRound || searchTerm) && (
              <Button variant="ghost" onClick={clearFilters} className="text-xs text-gray-500 hover:text-[#1a1a1a] p-0 font-medium" data-testid="clear-market-filters-btn">Clear all filters</Button>
            )}
          </aside>

          {/* Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
            ) : filteredMarkets.length === 0 ? (
              <div className="text-center py-20 text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>No markets found matching your criteria.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visibleMarkets.map((market) => (
                    <MarketCard
                      key={market.id}
                      market={market}
                      onClick={() => openMarketModal(market)}
                      isSaved={savedMarketIds.includes(market.id)}
                      onFollow={handleFollowMarket}
                    />
                  ))}
                </div>
                {pageCount > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-3" data-testid="market-pagination">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentSafePage === 1}
                      className="h-11 w-11 rounded-full border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-40"
                      data-testid="market-prev-page-btn"
                      aria-label="Previous market page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span
                      className="min-w-28 text-center text-sm font-semibold text-gray-600"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                      data-testid="market-page-count"
                    >
                      Page {currentSafePage} of {pageCount}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                      disabled={currentSafePage === pageCount}
                      className="h-11 w-11 rounded-full border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-40"
                      data-testid="market-next-page-btn"
                      aria-label="Next market page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-16 border-t border-gray-100 mt-12">
          <h2 className="text-2xl font-bold mb-5 text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Want to list your market?</h2>
          <Button className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-10 py-5 font-bold transition-all hover:-translate-y-0.5" onClick={() => window.location.href = "/register"} style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="list-market-btn">Register Your Market</Button>
        </div>
      </section>

      <MarketDetailModal
        market={selectedMarket}
        open={!!selectedMarket}
        onClose={closeMarketModal}
        isSaved={selectedMarket ? savedMarketIds.includes(selectedMarket.id) : false}
        onFollow={handleFollowMarket}
      />
    </div>
  );
};

const MarketCard = ({ market, onClick, isSaved, onFollow }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = getLogoUrl(market.logo);
  const hasLogo = logoUrl && !logoFailed;
  const season = formatSeason(market);
  const openDay = firstOpenDay(market.hours);
  const marketDay = formatMarketDay(market, openDay);
  const location = market.location || market.neighborhood || market.area || market.city;

  useEffect(() => {
    setLogoFailed(false);
  }, [market.logo]);

  return (
    <div onClick={onClick} className="bg-white border border-gray-100 rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group" data-testid={`market-card-${market.id}`}>
      <div className="relative">
        {marketDay && (
          <Badge className="absolute top-3 right-3 z-10 bg-[#1a1a1a] text-white text-xs font-normal rounded-full px-3">
            {marketDay}
          </Badge>
        )}
        <div className="relative flex items-center justify-center" style={{ width: '100%', height: '0', paddingBottom: '64.5%', backgroundImage: `url(${DEFAULT_BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          {hasLogo ? (
            <div className="absolute inset-0 flex items-center justify-center p-6 bg-white">
              <img src={logoUrl} alt={market.market_name} onError={() => setLogoFailed(true)} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" style={{ maxHeight: '80%', maxWidth: '80%' }} />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <span className="text-xl font-bold text-center text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name || "Unnamed Market"}</span>
            </div>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-bold text-base text-[#1a1a1a] group-hover:text-[#42D78A] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name}</h3>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFollow(market.id); }}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isSaved ? "bg-[#42D78A] border-[#42D78A] text-[#1a1a1a]" : "bg-white border-gray-200 text-gray-500 hover:border-[#42D78A] hover:text-[#1a1a1a]"}`}
            aria-label={isSaved ? "Unfollow market" : "Follow market"}
            data-testid={`follow-market-${market.id}`}
          >
            <Heart className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
          </button>
        </div>
        {market.description && (
          <p className="text-gray-500 text-sm mb-3 line-clamp-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{market.description}</p>
        )}
        <div className="space-y-1">
          {season && <p className="flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}><CalendarDays className="w-3 h-3" />{season}</p>}
          {location && <p className="flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}><MapPin className="w-3 h-3" />{location}</p>}
        </div>
      </div>
    </div>
  );
};

export const MarketDetailModal = ({ market, open, onClose, isSaved, onFollow }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  useEffect(() => {
    setLogoFailed(false);
    setSelectedBusiness(null);
  }, [market?.id, market?.logo]);

  if (!market) return null;
  const logoUrl = getLogoUrl(market.logo);
  const hasLogo = logoUrl && !logoFailed;
  const season = formatSeason(market);
  const hoursDisplay = typeof market.hours === "object" ? formatHoursDisplay(market.hours) : market.hours;
  const associatedBusinesses = Array.isArray(market.associated_businesses) ? market.associated_businesses : [];

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-0 rounded-2xl sm:w-full">
          <DialogTitle className="sr-only">{market.market_name} Details</DialogTitle>
          <DialogDescription className="sr-only">Detailed information about {market.market_name}</DialogDescription>
          <button onClick={onClose} className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 p-1 bg-white/80 backdrop-blur rounded-full" data-testid="close-market-modal-btn"><X className="w-5 h-5" /></button>
          <div className="p-4 sm:p-8">
            <div className="relative flex aspect-[1.55/1] w-full max-w-[465px] items-center justify-center mb-6 sm:mb-8 mx-auto rounded-xl overflow-hidden" style={{ backgroundImage: `url(${DEFAULT_BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              {hasLogo ? (
                <div className="absolute inset-0 flex items-center justify-center p-8 bg-white"><img src={logoUrl} alt={market.market_name} onError={() => setLogoFailed(true)} className="max-h-full max-w-full object-contain" /></div>
              ) : (
                <div className="flex items-center justify-center p-4"><span className="text-2xl md:text-3xl font-bold text-center text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name}</span></div>
              )}
            </div>
            <div className="space-y-5">
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Market Name</p>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <p className="text-xl font-bold text-[#1a1a1a] break-words" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name}</p>
                  <button
                    type="button"
                    onClick={() => onFollow(market.id)}
                    className={`shrink-0 inline-flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${isSaved ? "bg-[#42D78A] border-[#42D78A] text-[#1a1a1a]" : "bg-white border-gray-200 text-gray-600 hover:border-[#42D78A] hover:text-[#1a1a1a]"}`}
                    aria-label={isSaved ? "Unfollow market" : "Follow market"}
                    data-testid={`modal-follow-market-${market.id}`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    <Heart className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
                    {isSaved ? "Following" : "Follow"}
                  </button>
                </div>
              </div>
              {season && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Season</p><p className="text-sm font-medium">{season}</p></div>}
              {hoursDisplay && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Hours</p><p className="text-sm font-medium whitespace-pre-line">{hoursDisplay}</p></div>}
              {market.address && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Address</p><p className="text-sm font-medium flex items-start gap-1 break-words"><MapPin className="w-4 h-4 shrink-0 text-[#42D78A]" />{market.address}</p></div>}
              {(market.instagram || market.facebook || market.tiktok) && (
                <div>
                  <p className="text-xs tracking-widest text-gray-400 mb-2 uppercase">Social Media</p>
                  <SocialMediaIcons instagram={market.instagram} facebook={market.facebook} tiktok={market.tiktok} />
                </div>
              )}
              {market.description && (
                <div>
                  <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">About</p>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{market.description}</p>
                </div>
              )}
              {market.website && (
                <Button className="w-full bg-[#1a1a1a] hover:bg-black text-white rounded-full py-5 mt-4 font-bold" onClick={() => window.open(normalizeUrl(market.website), "_blank")} style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="visit-market-website-btn">
                  <Globe className="w-4 h-4 mr-2" />Visit Website
                </Button>
              )}
              {associatedBusinesses.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs tracking-widest text-gray-400 mb-3 uppercase">Market Vendors</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {associatedBusinesses.map((business) => (
                      <button
                        key={business.id}
                        type="button"
                        onClick={() => setSelectedBusiness(business)}
                        className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#42D78A]/50 hover:bg-white hover:shadow-sm"
                        data-testid={`associated-business-${business.id}`}
                      >
                        {getLogoUrl(business.logo) ? (
                          <img src={getLogoUrl(business.logo)} alt={`${business.business_name} logo`} className="h-12 w-12 shrink-0 rounded-xl object-cover bg-white" />
                        ) : (
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#42D78A]/15 text-[#157347]">
                            <Building2 className="h-5 w-5" />
                          </div>
                        )}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{business.business_name || "Unnamed Business"}</span>
                          <span className="mt-0.5 block truncate text-xs text-gray-500">{[business.business_type, ...(business.business_categories || [])].filter(Boolean).join(" · ") || "View business profile"}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BusinessDetailModal
        business={selectedBusiness}
        open={!!selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
        isSaved={false}
        onFollow={() => {}}
      />
    </>
  );
};

export default MarketDirectoryPage;
